import { PaginationInfo, PaymentAccount, PaymentAccountJSON } from '@scrubtech/models'
import LoopError from '../store/errors/LoopError'
import { fetchWithErrors, HTTPMethods, newRequest, parseResponse, token, urlForEndpoint } from './helpers'

export const getPaymentAccounts = async (sorting: string = 'id', page: number = 1, limit: number = 30, search: string): Promise<PaymentAccountsResponse> => {
  // Build request
  const url = urlForEndpoint(`payment-accounts`, {
    sorting,
    page,
    limit,
    search,
  })
  const request = newRequest(HTTPMethods.GET, token())

  // Fetch
  const response = await fetchWithErrors(url, request)

  // Handle errors and return response
  try {
    const { paymentAccounts: paymentAccountsJSON, paginationInfo } = await parseResponse(response)

    let paymentAccounts = paymentAccountsJSON.map((paymentAccountJSON: PaymentAccountJSON) => new PaymentAccount(paymentAccountJSON))

    return {
      paymentAccounts,
      paginationInfo,
      requestParams: {
        sorting,
        page,
        limit,
        search,
      },
    }

  } catch (err) {
    throw new LoopError(err, { sorting, page, limit, search })
  }
}

export const getPaymentAccountByID = async (paymentAccountID: number): Promise<PaymentAccount> => {

  // Build request
  const url = urlForEndpoint(`payment-accounts/${paymentAccountID}`)

  const request = newRequest(HTTPMethods.GET, token())

  // Fetch
  const response = await fetchWithErrors(url, request)

  // Handle errors and return response
  try {
    const { paymentAccount } = await parseResponse(response)
    return new PaymentAccount(paymentAccount as PaymentAccountJSON)
  } catch (err) {
    throw new LoopError(err, { paymentAccountID })
  }

}

export const putPaymentAccount = async (paymentAccountID: number, name: string, company: string | null, phoneNumber: string | null, emailAddress: string | null): Promise<PaymentAccount> => {
  // Build request
  const url = urlForEndpoint(`payment-accounts/${paymentAccountID}`)

  const request = newRequest(HTTPMethods.PUT, token())
  request.body = JSON.stringify({
    name,
    company,
    phoneNumber,
    emailAddress,
  })

  try {
    // Fetch
    const response = await fetchWithErrors(url, request)

    // Handle errors and return response
    const { paymentAccount } = await parseResponse(response)
    return new PaymentAccount(paymentAccount as PaymentAccountJSON)
  } catch (err) {
    throw new LoopError(err, { paymentAccountID, name, phoneNumber, emailAddress })
  }
}

export const deletePaymentAccount = async (paymentAccountID: number): Promise<PaymentAccount> => {
  // Build request
  const url = urlForEndpoint(`payment-accounts/${paymentAccountID}`)

  const request = newRequest(HTTPMethods.DELETE, token())

  // Fetch
  const response = await fetchWithErrors(url, request)

  // Handle errors and return response
  try {
    const { paymentAccount } = await parseResponse(response)
    return new PaymentAccount(paymentAccount as PaymentAccountJSON)
  } catch (err) {
    throw new LoopError(err, { paymentAccountID })
  }
}

export const postPaymentAccount = async (name: string, company: string | null, phoneNumber: string | null, emailAddress: string | null): Promise<PaymentAccount> => {
  // Build request
  const url = urlForEndpoint(`payment-accounts`)

  const request = newRequest(HTTPMethods.POST, token())
  request.body = JSON.stringify({
    name,
    company,
    phoneNumber,
    emailAddress,
  })

  try {
    // Fetch
    const response = await fetchWithErrors(url, request)

    // Handle errors and return response
    const { paymentAccount } = await parseResponse(response)
    return new PaymentAccount(paymentAccount as PaymentAccountJSON)
  } catch (err) {
    throw new LoopError(err, { name, phoneNumber, emailAddress })
  }
}

export interface PaymentAccountIDsResponse {
  paymentAccountIDs: number[]
  requestParams: PaymentAccountsRequestParams
}


export interface PaymentAccountsResponse {
  paymentAccounts: PaymentAccount[]
  paginationInfo: PaginationInfo
  requestParams: PaymentAccountsRequestParams
}

export interface PaymentAccountsErrorResponse {
  error: Error
  requestParams: PaymentAccountsRequestParams
}

export interface PaymentAccountsRequestParams {
  sorting: string
  page: number
  limit: number
  search: string
}

export interface PaymentAccountByIDRequestParams {
  paymentAccountID: number
}

export interface PaymentAccountByIDErrorResponse {
  error: Error
  requestParams: PaymentAccountByIDRequestParams
}

export interface PostPaymentAccountRequestParams {
  name: string
  company: string | null
  phoneNumber: string | null
  emailAddress: string | null
}

export interface PostPaymentAccountErrorResponse {
  error: Error
  requestParams: PostPaymentAccountRequestParams
}

export interface PutPaymentAccountRequestParams extends PostPaymentAccountRequestParams {
  paymentAccountID: number
}

export interface PutPaymentAccountErrorResponse {
  error: Error
  requestParams: PutPaymentAccountRequestParams
}
